'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTheme } from 'next-themes'
import { ThemeLogo } from './theme-logo'
import { ThemeToggle } from '@/components/theme-toggle'

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null)
  const [isDropdownClicked, setIsDropdownClicked] = useState(false)
  const [mounted, setMounted] = useState(false)
  const dropdownRef = useRef<HTMLUListElement>(null)
  const { resolvedTheme, setTheme } = useTheme()
  const [dropdownTimeoutId, setDropdownTimeoutId] = useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
        setIsDropdownClicked(false)
      }
    }

    // Handle scroll
    function handleScroll() {
      setActiveDropdown(null)
      setIsDropdownClicked(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const navItems = [
    {
      title: 'Opportunities',
      dropdown: [
        { title: 'Federal Contracts', href: '/federal/contracts' },
        { title: 'State Contracts', href: '/state/contracts' },
        { title: 'Regulations', href: '/regulations' },
      ],
    },
    {
      title: 'AI & Analytics',
      href: '/ai-analytics',
    },
    {
      title: 'Resources',
      dropdown: [
        { title: 'Features', href: '/features' },
        { title: 'Documentation', href: '/documentation' },
      ],
    },
  ]

  const handleMouseEnter = (index: number) => {
    if (!isDropdownClicked) {
      if (dropdownTimeoutId) {
        clearTimeout(dropdownTimeoutId)
        setDropdownTimeoutId(null)
      }
      setActiveDropdown(index)
    }
  }

  const handleMouseLeave = () => {
    if (!isDropdownClicked) {
      const timeoutId = setTimeout(() => {
        setActiveDropdown(null)
      }, 800)
      setDropdownTimeoutId(timeoutId)
    }
  }

  const handleDropdownClick = (index: number) => {
    if (activeDropdown === index) {
      setActiveDropdown(null)
      setIsDropdownClicked(false)
    } else {
      setActiveDropdown(index)
      setIsDropdownClicked(true)
    }
  }

  if (!mounted) {
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-20 items-center max-w-screen-2xl mx-auto">
          <div className="pl-4 md:pl-8">
            <ThemeLogo increasedSize={false} />
          </div>
          <div className="flex-1" />
          <div className="flex items-center space-x-4 pr-4 md:pr-8">
            <Button asChild variant="outline" size="lg">
              <Link href="/pricing">Pricing</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link href="/login">Log In</Link>
            </Button>
            <Button asChild size="lg">
              <Link href="/signup">Sign Up</Link>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 group">
      <div className="flex h-20 items-center max-w-screen-2xl mx-auto">
        <div className="pl-4 md:pl-8">
          <ThemeLogo increasedSize={false} />
        </div>
        <div className="hidden lg:flex items-center flex-1">
          <nav className="pl-8">
            <ul className="flex space-x-6" ref={dropdownRef}>
              {navItems.map((item, index) => (
                <li
                  key={index}
                  className="relative group"
                >
                  {item.dropdown ? (
                    <>
                      <button
                        className="flex items-center px-3 py-2 text-base hover:text-primary transition-colors"
                        onClick={() => handleDropdownClick(index)}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {item.title}
                        <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                      </button>
                      {activeDropdown === index && (
                        <div
                          className="absolute top-full left-0 mt-0 py-2 w-48 bg-background/95 border border-border rounded-md shadow-lg z-50 backdrop-blur-sm"
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <div className="absolute -top-2 left-0 right-0 h-2 bg-transparent" />
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.title}
                              href={dropdownItem.href}
                              className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                              {dropdownItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      href={item.href!}
                      className="flex items-center px-3 py-2 text-base hover:text-primary transition-colors"
                    >
                      {item.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex-1" />
          <div className="flex items-center space-x-4 pr-4 md:pr-8">
            <Button asChild variant="outline" size="lg">
              <Link href="/pricing">Pricing</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link href="/login">Log In</Link>
            </Button>
            <Button asChild size="lg">
              <Link href="/signup">Sign Up</Link>
            </Button>
            <ThemeToggle />
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="flex flex-1 items-center justify-between space-x-2 lg:hidden">
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="hover:bg-accent hover:text-accent-foreground active:scale-95 transition-transform"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-[81px] border-t border-border bg-background">
            <div className="container py-4">
              <nav className="mb-4">
                <ul className="space-y-2">
                  {navItems.map((item, index) => (
                    <li key={index}>
                      {item.dropdown ? (
                        <>
                          <span className="font-semibold text-base">{item.title}</span>
                          <ul className="ml-4 mt-1 space-y-1">
                            {item.dropdown.map((dropdownItem, dropdownIndex) => (
                              <li key={dropdownIndex}>
                                <Link
                                  href={dropdownItem.href}
                                  className="block py-1 text-sm hover:text-primary transition-colors"
                                >
                                  {dropdownItem.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <Link
                          href={item.href!}
                          className="block py-2 font-semibold text-base hover:text-primary transition-colors"
                        >
                          {item.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="space-y-2">
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link href="/pricing">Pricing</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link href="/login">Log In</Link>
                </Button>
                <Button asChild size="lg" className="w-full">
                  <Link href="/signup">Sign Up</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}